import React, { useState, useEffect } from 'react';
import { Box, Typography, Container, Grid, CircularProgress, Chip, Button, Dialog } from '@mui/material';
import PageBanner from '../../components/PageBanner';
import SEO from '../../components/SEO';
import SectionSidebar from '../../components/SectionSidebar';
import DirectionsBoatIcon from '@mui/icons-material/DirectionsBoat';
import SailingIcon from '@mui/icons-material/Sailing';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import EventNoteIcon from '@mui/icons-material/EventNote';
import WaterIcon from '@mui/icons-material/Water';
import AnchorIcon from '@mui/icons-material/Anchor';
import NearMeIcon from '@mui/icons-material/NearMe';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import BadgeIcon from '@mui/icons-material/Badge';
import ScheduleIcon from '@mui/icons-material/Schedule';
import GroupIcon from '@mui/icons-material/Group';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PalmTreeIcon from '@mui/icons-material/Park';
import StarIcon from '@mui/icons-material/Star';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import TrainIcon from '@mui/icons-material/Train';
import LaunchIcon from '@mui/icons-material/Launch';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CloseIcon from '@mui/icons-material/Close';
import { Helmet } from 'react-helmet-async';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';

const GOLD = '#d4af37';

interface BoatingItem {
  id: string;
  name: string;
  type: string;
  price: string;
  duration: string;
  location: string;
  description?: string;
  imageUrl?: string;
  website?: string;
  color?: string;
}

interface BoatingContent {
  title: string;
  description: string;
  boating: BoatingItem[];
  akalappuzhaImages?: string[];
}

const gradients: [string, string][] = [
  ['#06b6d4', '#0891b2'],
  ['#3b82f6', '#2563eb'],
  ['#0ea5e9', '#0284c7'],
  ['#8b5cf6', '#7c3aed'],
  ['#22c55e', '#16a34a'],
  ['#ec4899', '#db2777'],
];

const bookingTips = [
  { text: 'Book in advance during weekends & holidays', icon: ScheduleIcon },
  { text: 'Carry valid photo ID for all passengers', icon: BadgeIcon },
  { text: 'Arrive 15 min before your scheduled time', icon: AccessTimeIcon },
  { text: 'Check weather conditions before departure', icon: WaterIcon },
];

const safetyTips = [
  { text: 'Always wear the life jacket provided', icon: VerifiedUserIcon },
  { text: 'Follow instructions from the boat operator', icon: GroupIcon },
  { text: 'Avoid standing while the boat is moving', icon: HealthAndSafetyIcon },
  { text: 'Children must be accompanied by adults', icon: GroupIcon },
];

/* ─── Akalappuzha Tourism Data ─── */

const akalappuzhaHighlights = [
  {
    icon: WaterIcon,
    title: 'Backwater & Lake Tourism',
    accent: '#0ea5e9',
    points: [
      'A large lake formed from distributaries of rivers in North Kerala',
      'Peaceful village environment and fishing activities',
      'Shallow waters with fish like Karimeen (Pearl Spot)',
      'Popular filming location and scenic destination',
    ],
  },
  {
    icon: SailingIcon,
    title: 'Boating Experience',
    accent: '#3b82f6',
    points: [
      'Shikara-style boats and leisure rides available',
      'Scenic cruise through backwaters and small islands',
      'Safe boating with life jackets and rescue facilities',
    ],
  },
  {
    icon: WbSunnyIcon,
    title: 'Nature & Views',
    accent: '#22c55e',
    points: [
      'Island views (Edison Thuruth, etc.)',
      'Hilltop "Microwave viewpoint" for panoramic lake views',
      'Sunset scenery and fishing experience',
    ],
  },
];

const boatingServices = [
  {
    name: 'Seagull Cruise – Akalappuzha',
    icon: DirectionsBoatIcon,
    gradient: ['#0ea5e9', '#0284c7'],
    description: 'Local backwater boat cruise provider offering backwater rides and leisure boat trips. Family and group boating options available.',
    features: ['Backwater rides', 'Leisure boat trips', 'Family & group options', 'Contact booking available locally'],
    website: 'https://seagullcruise.in',
    websiteLabel: 'Seagull Cruise Akalappuzha Boating',
  },
  {
    name: 'Akalappuzha Boating Point / Tourism Service',
    icon: SailingIcon,
    gradient: ['#3b82f6', '#2563eb'],
    description: 'Local tourism boating facility with organized boat cruises and leisure trips. Safety ensured with life jackets and trained swimmers.',
    features: ['Organized boat cruises', 'Life jackets & trained swimmers', 'Lake-view stay & tourism facilities', 'Leisure trips'],
    website: 'https://akalappuzha.com',
    websiteLabel: 'Akalappuzha Tourism Official Site',
  },
];

export default function Boating() {
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState<BoatingContent>({
    title: 'Boating Nearby',
    description: 'Boating and water activities near Muchukunnu',
    boating: []
  });
  const [activeImg, setActiveImg] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  useEffect(() => {
    try {
      const unsubscribe = onSnapshot(doc(db, 'nearby_content', 'boating'), (docSnap) => {
        if (docSnap.exists()) {
          setContent(docSnap.data() as BoatingContent);
        }
        setLoading(false);
      });
      return () => unsubscribe();
    } catch (error) {
      console.error('Error loading boating:', error);
      setLoading(false);
    }
  }, []);

  // Use only images from Firebase admin dashboard
  const akalappuzhaImages = (content.akalappuzhaImages && content.akalappuzhaImages.length > 0)
    ? content.akalappuzhaImages
    : [];

  // Auto-rotate Akalappuzha images
  useEffect(() => {
    const timer = setInterval(() => {
      setActiveImg((prev) => (prev + 1) % akalappuzhaImages.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [akalappuzhaImages.length]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress sx={{ color: GOLD }} />
      </Box>
    );
  }

  const items = content.boating.map((b, i) => ({ ...b, gradient: gradients[i % gradients.length] }));

  return (
    <Box sx={{ bgcolor: '#edf8fb', minHeight: '100vh' }}>
      <SEO
        title="Akalappuzha Boating | Boat Booking & Enquiry"
        description="Akalappuzha boating \u2014 book boat rides, backwater cruises & leisure trips near Muchukunnu, Kozhikode. Akalappuzha boat booking enquiry, boat service, cruise timings & ticket info."
        path="/nearby/boating"
        keywords="akalappuzha boating, akalappuzha boating booking enquiry, akalappuzha, boat service, akalappuzha boat booking, akalappuzha boat ride, akalappuzha backwater cruise, akalappuzha boating point, boating near Kozhikode, boating near Muchukunnu temple, Kozhikode boating, water activities Kerala"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "TouristAttraction",
          "name": "Akalappuzha Boating Point",
          "alternateName": ["Akalappuzha Boating", "Akalappuzha Boat Service", "Akalappuzha Backwater Cruise"],
          "description": "Akalappuzha boating point near Muchukunnu, Kozhikode \u2014 enjoy backwater boat rides, leisure cruises and group boating. Book boat rides and enquire about schedules.",
          "url": "https://kottakovilakam.com/nearby/boating",
          "touristType": "Boating & Backwater Cruises",
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "Akalappuzha, Kozhikode",
            "addressRegion": "Kerala",
            "addressCountry": "IN"
          },
          "isAccessibleForFree": false,
          "availableLanguage": ["en", "ml"]
        })}</script>
      </Helmet>
      <PageBanner title="Boating & Water Activities" />
      <Container maxWidth="lg" sx={{ mt: { xs: -4, md: -6 }, position: 'relative', zIndex: 2, pb: 10 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={8} lg={9}>

            {/* HERO */}
            <Box
              sx={{
                borderRadius: '30px',
                overflow: 'hidden',
                mb: 4,
                boxShadow: '0 22px 64px rgba(10, 78, 95, 0.2)',
                background: 'linear-gradient(145deg, #0e4c61 0%, #0c6f82 48%, #19a9bf 100%)',
                position: 'relative',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  opacity: 0.13,
                  background:
                    'repeating-linear-gradient(120deg, rgba(255,255,255,0.18) 0px, rgba(255,255,255,0.18) 2px, transparent 2px, transparent 14px)',
                }}
              />
              <Box sx={{ position: 'absolute', top: -56, right: -56, width: 240, height: 240, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.16) 0%, transparent 70%)' }} />
              <Box sx={{ position: 'absolute', bottom: -48, left: -48, width: 190, height: 190, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.12) 0%, transparent 72%)' }} />

              <Box sx={{ px: { xs: 3, sm: 4 }, py: { xs: 4, sm: 5 }, position: 'relative', zIndex: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, mb: 2.5, flexWrap: 'wrap' }}>
                  <Box sx={{
                    width: 66, height: 66, borderRadius: '20px',
                    background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(10px)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    border: '2px solid rgba(255,255,255,0.25)',
                    boxShadow: '0 12px 30px rgba(9,49,59,0.28)',
                  }}>
                    <DirectionsBoatIcon sx={{ fontSize: 32, color: '#fff' }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 900, fontSize: { xs: '1.6rem', sm: '2rem' }, color: '#fff', lineHeight: 1.15, mb: 0.5, textShadow: '0 2px 12px rgba(0,0,0,0.2)' }}>
                      Boating & Water Activities
                    </Typography>
                    <Typography sx={{ fontSize: { xs: '0.82rem', sm: '0.9rem' }, color: 'rgba(255,255,255,0.68)', fontWeight: 500 }}>
                      Scenic rides, backwater cruises, and leisure experiences
                    </Typography>
                  </Box>
                </Box>
                <Typography sx={{ fontSize: { xs: '0.88rem', sm: '0.95rem' }, color: 'rgba(255,255,255,0.58)', lineHeight: 1.9, maxWidth: 560, mb: 2.5 }}>
                  Explore Akalappuzha and nearby waters with practical details for booking, safety, trip timings, and family-friendly options.
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2.2 }}>
                  <Chip icon={<AnchorIcon sx={{ fontSize: '16px !important', color: '#0b5f71 !important' }} />} label={`${items.length} Boating Option${items.length !== 1 ? 's' : ''}`} sx={{ bgcolor: 'rgba(255,255,255,0.95)', color: '#0d5f71', fontWeight: 700, fontSize: '0.78rem', height: 34, borderRadius: '12px', '& .MuiChip-icon': { ml: '8px' } }} />
                  <Chip icon={<WaterIcon sx={{ fontSize: '16px !important', color: '#99f6e4 !important' }} />} label="Backwater Tourism" sx={{ bgcolor: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.92)', fontWeight: 700, fontSize: '0.78rem', height: 34, borderRadius: '12px', border: '1px solid rgba(255,255,255,0.18)', '& .MuiChip-icon': { ml: '8px' } }} />
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0,1fr))' }, gap: 1.3 }}>
                  {[
                    { label: 'Boating Directory', value: `${items.length}`, icon: DirectionsBoatIcon },
                    { label: 'Travel Focus', value: 'Backwater Circuits', icon: WaterIcon },
                    { label: 'Planning Ready', value: 'Booking & Safety', icon: VerifiedUserIcon },
                  ].map((metric) => {
                    const MIcon = metric.icon;
                    return (
                      <Box key={metric.label} sx={{ borderRadius: '14px', p: 1.4, bgcolor: 'rgba(255,255,255,0.13)', border: '1px solid rgba(255,255,255,0.16)' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.1 }}>
                          <Box sx={{ width: 28, height: 28, borderRadius: '9px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <MIcon sx={{ fontSize: 16, color: '#fff' }} />
                          </Box>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ color: '#fff', fontSize: '0.8rem', fontWeight: 700, lineHeight: 1.2 }}>{metric.value}</Typography>
                            <Typography sx={{ color: 'rgba(255,255,255,0.74)', fontSize: '0.7rem', lineHeight: 1.2 }}>{metric.label}</Typography>
                          </Box>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </Box>

            {/* ════════ AKALAPPUZHA TOURISM SECTION ════════ */}
            <Box sx={{ mb: 4 }}>
              {/* Section Title */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Box sx={{
                  width: 48, height: 48, borderRadius: '16px',
                  background: 'linear-gradient(135deg, #059669, #10b981)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: '0 8px 24px rgba(5,150,105,0.3)',
                }}>
                  <PalmTreeIcon sx={{ fontSize: 26, color: '#fff' }} />
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 900, fontSize: { xs: '1.4rem', sm: '1.7rem' }, color: '#0f172a', lineHeight: 1.15 }}>
                    Akalappuzha Tourism
                  </Typography>
                  <Typography sx={{ fontSize: '0.85rem', color: '#64748b', fontWeight: 500 }}>
                    Kozhikode, Kerala
                  </Typography>
                </Box>
                <Chip icon={<StarIcon sx={{ fontSize: '14px !important', color: '#f59e0b !important' }} />} label="4★" size="small" sx={{ bgcolor: '#fef3c7', color: '#92400e', fontWeight: 800, fontSize: '0.8rem', height: 30, borderRadius: '10px', ml: 'auto', '& .MuiChip-icon': { ml: '6px' } }} />
              </Box>

              {/* Image Gallery + Overview */}
              <Box sx={{
                bgcolor: '#fff', borderRadius: '24px', overflow: 'hidden',
                border: '1px solid rgba(9,92,126,0.14)', boxShadow: '0 4px 24px rgba(9,69,106,0.1)',
                mb: 3,
              }}>
                {/* Image Carousel - only show if images exist */}
                {akalappuzhaImages.length > 0 && (
                <Box sx={{ position: 'relative', width: '100%', height: { xs: 220, sm: 320 }, overflow: 'hidden', cursor: 'pointer' }}
                  onClick={() => { setLightboxIndex(activeImg); setLightboxOpen(true); }}
                >
                  {akalappuzhaImages.map((img, idx) => (
                    <Box
                      key={idx}
                      component="img"
                      src={img}
                      alt={`Akalappuzha view ${idx + 1}`}
                      sx={{
                        position: 'absolute', inset: 0, width: '100%', height: '100%',
                        objectFit: 'cover',
                        opacity: activeImg === idx ? 1 : 0,
                        transition: 'opacity 1s ease-in-out',
                      }}
                    />
                  ))}
                  {/* Left / Right Arrows */}
                  {akalappuzhaImages.length > 1 && (
                  <>
                    <Box
                      onClick={() => setActiveImg((prev) => (prev - 1 + akalappuzhaImages.length) % akalappuzhaImages.length)}
                      sx={{
                        position: 'absolute', top: '50%', left: 12, transform: 'translateY(-50%)',
                        width: 38, height: 38, borderRadius: '50%',
                        bgcolor: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(6px)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        cursor: 'pointer', zIndex: 2,
                        transition: 'all 200ms',
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.7)', transform: 'translateY(-50%) scale(1.1)' },
                      }}
                    >
                      <ArrowBackIosNewIcon sx={{ fontSize: 18, color: '#fff' }} />
                    </Box>
                    <Box
                      onClick={() => setActiveImg((prev) => (prev + 1) % akalappuzhaImages.length)}
                      sx={{
                        position: 'absolute', top: '50%', right: 12, transform: 'translateY(-50%)',
                        width: 38, height: 38, borderRadius: '50%',
                        bgcolor: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(6px)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        cursor: 'pointer', zIndex: 2,
                        transition: 'all 200ms',
                        '&:hover': { bgcolor: 'rgba(0,0,0,0.7)', transform: 'translateY(-50%) scale(1.1)' },
                      }}
                    >
                      <ArrowForwardIosIcon sx={{ fontSize: 18, color: '#fff' }} />
                    </Box>
                  </>
                  )}
                  {/* Gradient overlay */}
                  <Box sx={{
                    position: 'absolute', bottom: 0, left: 0, right: 0, height: '50%',
                    background: 'linear-gradient(to top, rgba(0,0,0,0.5) 0%, transparent 100%)',
                  }} />
                  {/* Image dots */}
                  {akalappuzhaImages.length > 1 && (
                  <Box sx={{ position: 'absolute', bottom: 12, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 1 }}>
                    {akalappuzhaImages.map((_, idx) => (
                      <Box
                        key={idx}
                        onClick={() => setActiveImg(idx)}
                        sx={{
                          width: activeImg === idx ? 24 : 8, height: 8, borderRadius: '4px',
                          bgcolor: activeImg === idx ? '#fff' : 'rgba(255,255,255,0.5)',
                          cursor: 'pointer', transition: 'all 300ms',
                        }}
                      />
                    ))}
                  </Box>
                  )}
                  {/* Badge overlay */}
                  <Box sx={{ position: 'absolute', top: 16, left: 16 }}>
                    <Chip
                      icon={<PhotoCameraIcon sx={{ fontSize: '14px !important', color: '#fff !important' }} />}
                      label="Scenic Destination"
                      size="small"
                      sx={{
                        bgcolor: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(10px)',
                        color: '#fff', fontWeight: 600, fontSize: '0.72rem', height: 28,
                        borderRadius: '8px', border: '1px solid rgba(255,255,255,0.15)',
                        '& .MuiChip-icon': { ml: '6px' },
                      }}
                    />
                  </Box>
                </Box>
                )}

                {/* Overview Text */}
                <Box sx={{ px: { xs: 2.5, sm: 3.5 }, py: 3 }}>
                  <Typography sx={{ fontSize: { xs: '0.9rem', sm: '0.95rem' }, color: '#475569', lineHeight: 1.9, mb: 2.5 }}>
                    Akalappuzha is a beautiful backwater destination situated in Muchukunnu, near Koyilandy (Kozhikode district, Kerala).
                    It is often called the <strong>"Kuttanad of Kozhikode"</strong> because of its calm lake views,
                    village scenery, and boating experience similar to Kerala's famous backwaters.
                  </Typography>

                  {/* Location Info */}
                  <Box sx={{
                    bgcolor: '#f0fdf4', borderRadius: '16px', p: 2.5,
                    border: '1px solid rgba(22,163,74,0.12)',
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                      <LocationOnIcon sx={{ fontSize: 20, color: '#16a34a' }} />
                      <Typography sx={{ fontWeight: 800, fontSize: '0.9rem', color: '#15803d' }}>Location</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
                      {[
                        'Muchukunnu, near Payyoli, Thikkodi, Keezhariyur (Kozhikode district)',
                        'Easily reachable from NH66 highway',
                      ].map((loc, i) => (
                        <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: '#16a34a', flexShrink: 0 }} />
                          <Typography sx={{ fontSize: '0.82rem', color: '#475569', fontWeight: 500 }}>{loc}</Typography>
                        </Box>
                      ))}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TrainIcon sx={{ fontSize: 14, color: '#16a34a', flexShrink: 0 }} />
                        <Typography sx={{ fontSize: '0.82rem', color: '#475569', fontWeight: 500 }}>
                          Nearest railway stations: Payyoli, Thikkodi, Koyilandy
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>

              {/* ── Why Akalappuzha is Famous ── */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                  <StarIcon sx={{ fontSize: 22, color: GOLD }} />
                  <Typography sx={{ fontWeight: 800, fontSize: { xs: '0.95rem', md: '1.15rem' }, color: '#0f172a' }}>
                    Why Akalappuzha is Famous
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                  {akalappuzhaHighlights.map((hl, idx) => {
                    const HlIcon = hl.icon;
                    return (
                      <Box key={idx} sx={{
                        bgcolor: '#fff', borderRadius: '20px', overflow: 'hidden',
                        border: '1px solid rgba(16,98,122,0.14)', boxShadow: '0 2px 16px rgba(9,69,106,0.09)',
                        transition: 'all 300ms',
                        '&:hover': { boxShadow: '0 8px 32px rgba(9,69,106,0.16)', transform: 'translateY(-2px)' },
                      }}>
                        <Box sx={{
                          background: `linear-gradient(135deg, ${hl.accent}, ${hl.accent}cc)`,
                          px: 2.5, py: 1.8, display: 'flex', alignItems: 'center', gap: 1.5,
                        }}>
                          <Box sx={{
                            width: 38, height: 38, borderRadius: '12px',
                            background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(10px)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            border: '1px solid rgba(255,255,255,0.25)',
                          }}>
                            <HlIcon sx={{ fontSize: 20, color: '#fff' }} />
                          </Box>
                          <Typography sx={{ fontWeight: 800, fontSize: { xs: '0.88rem', md: '1rem' }, color: '#fff' }}>
                            {hl.title}
                          </Typography>
                        </Box>
                        <Box sx={{ px: 2.5, py: 2 }}>
                          {hl.points.map((pt, pi) => (
                            <Box key={pi} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.2, py: 0.6 }}>
                              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: hl.accent, mt: 0.8, flexShrink: 0 }} />
                              <Typography sx={{ fontSize: '0.85rem', color: '#475569', lineHeight: 1.7, fontWeight: 500 }}>
                                {pt}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>

              {/* ── Boating Companies / Services ── */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                  <DirectionsBoatIcon sx={{ fontSize: 22, color: '#3b82f6' }} />
                  <Typography sx={{ fontWeight: 800, fontSize: { xs: '0.95rem', md: '1.15rem' }, color: '#0f172a' }}>
                    Akalappuzha Boating Companies / Services
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                  {boatingServices.map((svc, idx) => {
                    const SvcIcon = svc.icon;
                    const [gStart, gEnd] = svc.gradient;
                    return (
                      <Box key={idx} sx={{
                        bgcolor: '#fff', borderRadius: '20px', overflow: 'hidden',
                        border: '1px solid rgba(17,103,133,0.14)', boxShadow: '0 4px 24px rgba(9,69,106,0.1)',
                        transition: 'all 400ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                        '&:hover': { transform: 'translateY(-4px)', boxShadow: `0 16px 48px rgba(9,69,106,0.18), 0 0 0 1px ${gStart}25` },
                      }}>
                        <Box sx={{
                          background: `linear-gradient(135deg, ${gStart}, ${gEnd})`,
                          px: { xs: 2.5, sm: 3 }, py: { xs: 2, sm: 2.5 },
                          position: 'relative', overflow: 'hidden',
                        }}>
                          <Box sx={{ position: 'absolute', top: -15, right: -15, width: 80, height: 80, borderRadius: '50%', background: 'rgba(255,255,255,0.1)' }} />
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, position: 'relative', zIndex: 2 }}>
                            <Box sx={{
                              width: 46, height: 46, borderRadius: '14px',
                              background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(10px)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              border: '1px solid rgba(255,255,255,0.25)',
                            }}>
                              <SvcIcon sx={{ fontSize: 24, color: '#fff' }} />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                              <Typography sx={{ fontWeight: 800, fontSize: { xs: '1rem', sm: '1.1rem' }, color: '#fff', lineHeight: 1.2 }}>
                                {svc.name}
                              </Typography>
                              <Chip
                                label="Boating Service"
                                size="small"
                                sx={{
                                  mt: 0.5, bgcolor: 'rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.9)',
                                  fontWeight: 600, fontSize: '0.68rem', height: 22, borderRadius: '6px',
                                  border: '1px solid rgba(255,255,255,0.15)',
                                }}
                              />
                            </Box>
                          </Box>
                        </Box>

                        <Box sx={{ px: { xs: 2.5, sm: 3 }, py: 2.5 }}>
                          <Typography sx={{ fontSize: '0.88rem', color: '#475569', lineHeight: 1.8, mb: 2 }}>
                            {svc.description}
                          </Typography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                            {svc.features.map((feat, fi) => (
                              <Chip
                                key={fi}
                                label={feat}
                                size="small"
                                sx={{
                                  bgcolor: `${gStart}10`, color: gStart, fontWeight: 600,
                                  fontSize: '0.73rem', height: 28, borderRadius: '8px',
                                  border: `1px solid ${gStart}20`,
                                }}
                              />
                            ))}
                          </Box>
                          <Button
                            href={svc.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            endIcon={<LaunchIcon sx={{ fontSize: '16px !important' }} />}
                            sx={{
                              textTransform: 'none', fontWeight: 700, fontSize: '0.82rem',
                              color: gStart, borderRadius: '10px', px: 2, py: 0.8,
                              border: `1.5px solid ${gStart}30`,
                              '&:hover': { bgcolor: `${gStart}08`, borderColor: gStart },
                            }}
                          >
                            {svc.websiteLabel}
                          </Button>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </Box>

            {/* ════════ FIREBASE BOATING CARDS ════════ */}
            {items.length > 0 && (
              <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                  <AnchorIcon sx={{ fontSize: 22, color: '#0891b2' }} />
                  <Typography sx={{ fontWeight: 800, fontSize: { xs: '0.95rem', md: '1.15rem' }, color: '#0f172a' }}>
                    More Boating Activities
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {items.map((b) => {
                    const [gStart, gEnd] = b.gradient;
                    return (
                      <Box key={b.id || b.name} sx={{
                        bgcolor: '#fff', borderRadius: '24px', overflow: 'hidden',
                        border: '1px solid rgba(10,95,126,0.14)', boxShadow: '0 4px 24px rgba(9,69,106,0.1)',
                        transition: 'all 400ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                        '&:hover': { transform: 'translateY(-6px)', boxShadow: `0 20px 60px rgba(9,69,106,0.2), 0 0 0 1px ${gStart}25` },
                      }}>
                        {/* Image Banner if available */}
                        {b.imageUrl && (
                          <Box sx={{ position: 'relative', width: '100%', height: { xs: 180, sm: 220 }, overflow: 'hidden' }}>
                            <Box
                              component="img"
                              src={b.imageUrl}
                              alt={b.name}
                              sx={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 600ms ease' }}
                              onMouseOver={(e: React.MouseEvent<HTMLImageElement>) => { (e.target as HTMLImageElement).style.transform = 'scale(1.05)'; }}
                              onMouseOut={(e: React.MouseEvent<HTMLImageElement>) => { (e.target as HTMLImageElement).style.transform = 'scale(1)'; }}
                            />
                            <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '50%', background: 'linear-gradient(to top, rgba(0,0,0,0.5) 0%, transparent 100%)' }} />
                            <Box sx={{ position: 'absolute', bottom: 12, left: 16 }}>
                              <Typography sx={{ fontWeight: 800, fontSize: { xs: '0.92rem', md: '1.1rem' }, color: '#fff', textShadow: '0 2px 8px rgba(0,0,0,0.3)' }}>{b.name}</Typography>
                              <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.8)', fontWeight: 500 }}>{b.location}</Typography>
                            </Box>
                          </Box>
                        )}

                        {/* Gradient Header (shown if no image) */}
                        {!b.imageUrl && (
                          <Box sx={{
                            background: `linear-gradient(135deg, ${gStart}, ${gEnd})`,
                            px: { xs: 2.5, sm: 3 }, py: { xs: 2, sm: 2.5 },
                            position: 'relative', overflow: 'hidden',
                          }}>
                            <Box sx={{ position: 'absolute', top: -15, right: -15, width: 80, height: 80, borderRadius: '50%', background: 'rgba(255,255,255,0.1)' }} />
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, position: 'relative', zIndex: 2 }}>
                              <Box sx={{ width: 46, height: 46, borderRadius: '14px', background: 'rgba(255,255,255,0.2)', backdropFilter: 'blur(10px)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(255,255,255,0.25)' }}>
                                <SailingIcon sx={{ fontSize: 24, color: '#fff' }} />
                              </Box>
                              <Box sx={{ flex: 1 }}>
                                <Typography sx={{ fontWeight: 800, fontSize: { xs: '1.05rem', sm: '1.15rem' }, color: '#fff', lineHeight: 1.2 }}>
                                  {b.name}
                                </Typography>
                                <Typography sx={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.75)', fontWeight: 500 }}>
                                  {b.location}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        )}

                        <Box sx={{ px: { xs: 2.5, sm: 3 }, py: 2.5, bgcolor: '#f8fcff' }}>
                          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                            <Chip icon={<NearMeIcon sx={{ fontSize: '15px !important', color: `${gStart} !important` }} />} label={b.type} size="small" sx={{ bgcolor: `${gStart}10`, color: gStart, fontWeight: 700, fontSize: '0.75rem', height: 30, borderRadius: '10px', border: `1px solid ${gStart}20`, '& .MuiChip-icon': { ml: '6px' } }} />
                            {b.duration && (
                              <Chip icon={<AccessTimeIcon sx={{ fontSize: '14px !important', color: '#64748b !important' }} />} label={b.duration} size="small" sx={{ bgcolor: 'rgba(100,116,139,0.08)', color: '#64748b', fontWeight: 600, fontSize: '0.75rem', height: 30, borderRadius: '10px', border: '1px solid rgba(100,116,139,0.12)', '& .MuiChip-icon': { ml: '6px' } }} />
                            )}
                            {b.price && (
                              <Chip icon={<CurrencyRupeeIcon sx={{ fontSize: '14px !important', color: '#16a34a !important' }} />} label={b.price} size="small" sx={{ bgcolor: 'rgba(22,163,106,0.08)', color: '#16a34a', fontWeight: 700, fontSize: '0.75rem', height: 30, borderRadius: '10px', border: '1px solid rgba(22,163,106,0.12)', '& .MuiChip-icon': { ml: '6px' } }} />
                            )}
                          </Box>
                          {b.description && (
                            <Typography sx={{ fontSize: '0.88rem', color: '#4f677c', lineHeight: 1.8, letterSpacing: '0.01em' }}>
                              {b.description}
                            </Typography>
                          )}
                          {b.website && (
                            <Button
                              href={b.website}
                              target="_blank"
                              rel="noopener noreferrer"
                              endIcon={<LaunchIcon sx={{ fontSize: '14px !important' }} />}
                              size="small"
                              sx={{
                                mt: 1.5, textTransform: 'none', fontWeight: 700, fontSize: '0.78rem',
                                color: gStart, borderRadius: '8px', px: 1.5,
                                border: `1.5px solid ${gStart}25`,
                                '&:hover': { bgcolor: `${gStart}08` },
                              }}
                            >
                              Visit Website
                            </Button>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            )}

            {/* TIPS FOOTER */}
            <Box sx={{
              background: 'linear-gradient(145deg, #103447 0%, #19536f 45%, #0c2632 100%)',
              borderRadius: '24px', p: { xs: 3, sm: 3.5 }, position: 'relative', overflow: 'hidden',
              border: '1px solid rgba(255,255,255,0.06)', boxShadow: '0 8px 32px rgba(0,0,0,0.16)',
            }}>
              <Box sx={{ position: 'absolute', inset: 0, opacity: 0.06, backgroundImage: 'linear-gradient(115deg, rgba(255,255,255,0.15) 0 2px, transparent 2px 12px)' }} />
              <Box sx={{ position: 'absolute', top: -30, right: -30, width: 120, height: 120, borderRadius: '50%', background: `radial-gradient(circle, ${GOLD}18 0%, transparent 70%)` }} />
              <Box sx={{ position: 'relative', zIndex: 2 }}>
                <Grid container spacing={3}>
                  {[
                    { title: 'Booking Tips', icon: EventNoteIcon, items: bookingTips, accent: '#3b82f6' },
                    { title: 'Safety Guidelines', icon: HealthAndSafetyIcon, items: safetyTips, accent: '#22c55e' },
                  ].map((sec) => {
                    const SecIcon = sec.icon;
                    return (
                      <Grid item xs={12} sm={6} key={sec.title}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                          <Box sx={{ width: 36, height: 36, borderRadius: '10px', background: `linear-gradient(135deg, ${sec.accent}, ${sec.accent}cc)`, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: `0 4px 16px ${sec.accent}30` }}>
                            <SecIcon sx={{ fontSize: 18, color: '#fff' }} />
                          </Box>
                          <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', color: '#fff' }}>{sec.title}</Typography>
                        </Box>
                        {sec.items.map((t, j) => {
                          const TIcon = t.icon;
                          return (
                            <Box key={j} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1, px: 1, borderRadius: '10px', transition: 'all 250ms', border: '1px solid rgba(255,255,255,0.06)', bgcolor: 'rgba(255,255,255,0.02)', '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' } }}>
                              <Box sx={{ width: 24, height: 24, borderRadius: '7px', bgcolor: `${GOLD}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <TIcon sx={{ fontSize: 13, color: GOLD }} />
                              </Box>
                              <Typography sx={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.75)', fontWeight: 500, lineHeight: 1.6 }}>{t.text}</Typography>
                            </Box>
                          );
                        })}
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            </Box>

          </Grid>
          <Grid item xs={12} md={4} lg={3}>
            <SectionSidebar sectionKey="nearby" activePath="/nearby/boating" />
          </Grid>
        </Grid>
      </Container>

      {/* ═══ FULLSCREEN LIGHTBOX ═══ */}
      <Dialog
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        maxWidth={false}
        PaperProps={{
          sx: {
            bgcolor: 'transparent', boxShadow: 'none', m: 0,
            maxWidth: '95vw', maxHeight: '95vh', overflow: 'visible',
          }
        }}
        slotProps={{ backdrop: { sx: { bgcolor: 'rgba(0,0,0,0.92)' } } }}
      >
        <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {/* Close button */}
          <Box
            onClick={() => setLightboxOpen(false)}
            sx={{
              position: 'absolute', top: -20, right: -20, zIndex: 10,
              width: 40, height: 40, borderRadius: '50%',
              bgcolor: 'rgba(255,255,255,0.9)', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
              transition: 'all 200ms',
              '&:hover': { bgcolor: '#fff', transform: 'scale(1.1)' },
            }}
          >
            <CloseIcon sx={{ fontSize: 20, color: '#333' }} />
          </Box>

          {/* Left arrow */}
          {akalappuzhaImages.length > 1 && (
            <Box
              onClick={(e) => { e.stopPropagation(); setLightboxIndex((prev) => (prev - 1 + akalappuzhaImages.length) % akalappuzhaImages.length); }}
              sx={{
                position: 'absolute', left: { xs: 4, sm: -56 }, top: '50%', transform: 'translateY(-50%)',
                width: 44, height: 44, borderRadius: '50%',
                bgcolor: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(8px)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', zIndex: 10,
                border: '1px solid rgba(255,255,255,0.2)',
                transition: 'all 200ms',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.25)', transform: 'translateY(-50%) scale(1.1)' },
              }}
            >
              <ArrowBackIosNewIcon sx={{ fontSize: 20, color: '#fff' }} />
            </Box>
          )}

          {/* Image */}
          {akalappuzhaImages[lightboxIndex] && (
            <img
              src={akalappuzhaImages[lightboxIndex]}
              alt={`Akalappuzha view ${lightboxIndex + 1}`}
              style={{
                maxWidth: '92vw', maxHeight: '85dvh',
                borderRadius: '16px', display: 'block',
                objectFit: 'contain',
                boxShadow: '0 8px 40px rgba(0,0,0,0.4)',
              }}
            />
          )}

          {/* Right arrow */}
          {akalappuzhaImages.length > 1 && (
            <Box
              onClick={(e) => { e.stopPropagation(); setLightboxIndex((prev) => (prev + 1) % akalappuzhaImages.length); }}
              sx={{
                position: 'absolute', right: { xs: 4, sm: -56 }, top: '50%', transform: 'translateY(-50%)',
                width: 44, height: 44, borderRadius: '50%',
                bgcolor: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(8px)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', zIndex: 10,
                border: '1px solid rgba(255,255,255,0.2)',
                transition: 'all 200ms',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.25)', transform: 'translateY(-50%) scale(1.1)' },
              }}
            >
              <ArrowForwardIosIcon sx={{ fontSize: 20, color: '#fff' }} />
            </Box>
          )}

          {/* Image counter */}
          {akalappuzhaImages.length > 1 && (
            <Box sx={{
              position: 'absolute', bottom: -36, left: '50%', transform: 'translateX(-50%)',
              display: 'flex', alignItems: 'center', gap: 1.5,
            }}>
              {/* Dot indicators */}
              <Box sx={{ display: 'flex', gap: 0.8 }}>
                {akalappuzhaImages.map((_, idx) => (
                  <Box
                    key={idx}
                    onClick={(e) => { e.stopPropagation(); setLightboxIndex(idx); }}
                    sx={{
                      width: lightboxIndex === idx ? 22 : 8, height: 8,
                      borderRadius: '4px', cursor: 'pointer',
                      bgcolor: lightboxIndex === idx ? '#fff' : 'rgba(255,255,255,0.4)',
                      transition: 'all 300ms',
                      '&:hover': { bgcolor: lightboxIndex === idx ? '#fff' : 'rgba(255,255,255,0.65)' },
                    }}
                  />
                ))}
              </Box>
              <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.78rem', fontWeight: 600, ml: 1 }}>
                {lightboxIndex + 1} / {akalappuzhaImages.length}
              </Typography>
            </Box>
          )}
        </Box>
      </Dialog>
    </Box>
  );
}
